import React from "react";

import { useTranslation } from "react-i18next";

import ButtonGroup from "../../../../../../components/ui/button-group/ButtonGroup";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { useCardsStore } from "../../../../../../stores/use-cards-store";
import { BOOLEAN_OPTIONS, CORNERS_OPTIONS } from "./options";
import { CARD_TYPE, CARDS_PROP, DESIGNS_OPTIONS } from "./defaults";

const CardsOptions = () => {
  const { t } = useTranslation();
  const { data, updateCardsConfig, updateCardsData } = useCardsStore(
    (state) => ({
      data: state.data,
      updateCardsConfig: state.updateCardsConfig,
      updateCardsData: state.updateCardsData,
    })
  );

  const { card, config } = data[0] || { config: { layout: [] } };

  const hasLabelColor =
    config.layout.includes(CARDS_PROP.LABEL) ||
    DESIGNS_OPTIONS.LABEL_COLOR.includes(config.design);

  const hasGlobalColor = [CARD_TYPE.STEPS, CARD_TYPE.NUMBER].includes(
    config.type
  );

  const showColorPicker = hasLabelColor || hasGlobalColor;

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>

        {DESIGNS_OPTIONS.ICON.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.ICON)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.ICON];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.ICON
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.SUBTEXT.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.TEXT)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.TEXT];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.TEXT
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.LABEL.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.LABEL)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.LABEL];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.LABEL
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.ARROW.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("arrow")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.ARROW)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.ARROW];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.ARROW
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.LINK_TEXT.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("link-text")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.LINK_TEXT)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.LINK_TEXT];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.LINK_TEXT
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={config[CARDS_PROP.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                updateCardsConfig({ key: CARDS_PROP.BORDER_RADIUS, value })
              }
              onChange={({ target: { value } }) =>
                updateCardsConfig({ key: CARDS_PROP.BORDER_RADIUS, value })
              }
            />
          </OptionContent>
        </OptionContainer>

        {showColorPicker && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                value={card[CARDS_PROP.COLORS]}
                onChange={(value) =>
                  updateCardsData({ key: CARDS_PROP.COLORS, value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
};

export default CardsOptions;
